import { NavContext, useIonActionSheet } from '@ionic/react';
import React, { useMemo } from 'react';
import { useContext } from 'react';

import { useDentist } from '@/contexts/DentistContext';
import { Request } from '@/data/Requests';
import { OVERVIEW_TABLE } from '@/utils/constants';
import { formatAppointmentPurpose } from '@/utils/helpers/formatAppointmentPurpose';
import { formatDentistName } from '@/utils/helpers/formatDentistName';
import { parseTime } from '@/utils/helpers/parseTime';
import { ROUTES } from '@/utils/routes';
import { TimeSlot } from '@/utils/types';
import {
  addHours,
  differenceInMinutes,
  endOfHour,
  format,
  getMinutes,
  isBefore,
  isEqual,
  max,
  min,
  startOfHour
} from 'date-fns';

import './OverviewTable.css';

export interface OverviewTableProps {
  appointmentsByDentist: Record<string, Request[]>;
  onCloseModal?: () => void;
}

const OverviewTable: React.FC<OverviewTableProps> = ({
  appointmentsByDentist,
  onCloseModal
}) => {
  const { dentists, selectDentist } = useDentist();
  const [present] = useIonActionSheet();
  const { navigate } = useContext(NavContext);

  const onAppointmentClick = (appointment: Request) => {
    const displayPurpose = formatAppointmentPurpose(appointment);
    const startTime = format(parseTime(appointment.start_time), 'h:mma');
    const endTime = format(parseTime(appointment.end_time), 'h:mma');
    const timeRange = `${startTime} - ${endTime}`;

    present({
      header: displayPurpose,
      subHeader: timeRange,
      buttons: [
        {
          text: 'View Appointment',
          handler: async () => {
            if (onCloseModal) {
              onCloseModal();
            }

            const appointmentDentist = dentists.find(
              (d) => d.id === appointment.dentist_id
            );
            if (appointmentDentist) {
              await selectDentist(appointmentDentist);
            }

            navigate(
              `${ROUTES.CALENDAR}?selectedDate=${appointment.date}&focusedRequestId=${appointment.id}`
            );
          }
        },
        {
          text: 'Nevermind',
          role: 'cancel'
        }
      ]
    });
  };

  const { timeSlots, minTime, shouldSkipFirstColumn } = useMemo(() => {
    const allAppointments = Object.values(appointmentsByDentist).flat();

    if (allAppointments.length === 0) {
      const defaultStart = parseTime('09:00');
      const timeSlots: TimeSlot[] = [];
      let position = -OVERVIEW_TABLE.HALF_SLOT_WIDTH;
      let currentTime = defaultStart;

      for (let i = 0; i < OVERVIEW_TABLE.MIN_TIME_COLUMNS; i++) {
        timeSlots.push({
          time: format(currentTime, 'h:mm a'),
          position,
          width: OVERVIEW_TABLE.SLOT_WIDTH
        });
        currentTime = addHours(currentTime, 1);
        position += OVERVIEW_TABLE.SLOT_WIDTH;
      }

      return { timeSlots, minTime: '09:00', shouldSkipFirstColumn: false };
    }

    const times = allAppointments
      .map((appointment) => [appointment.start_time, appointment.end_time])
      .flat()
      .filter(Boolean)
      .map((time) => parseTime(time));

    const minTimeDate = min(times);
    const minTime = format(minTimeDate, 'HH:mm');
    const timeSlots: TimeSlot[] = [];
    const minTimeMoment = minTimeDate;

    const firstAppointmentStart = min(
      allAppointments.map((appointment) => parseTime(appointment.start_time))
    );
    const shouldSkipFirstColumn = getMinutes(firstAppointmentStart) === 30;

    const latestEndTime = max(
      allAppointments.map((appointment) => parseTime(appointment.end_time))
    );
    const needsExtraColumn = getMinutes(latestEndTime) >= 30;
    const end = needsExtraColumn
      ? startOfHour(addHours(latestEndTime, 1))
      : endOfHour(latestEndTime);

    const start = shouldSkipFirstColumn
      ? startOfHour(addHours(minTimeMoment, 1))
      : startOfHour(minTimeMoment);

    let position = shouldSkipFirstColumn ? 0 : -OVERVIEW_TABLE.HALF_SLOT_WIDTH;
    let currentTime = start;

    while (isBefore(currentTime, end) || isEqual(currentTime, end)) {
      timeSlots.push({
        time: format(currentTime, 'h:mm a'),
        position,
        width: OVERVIEW_TABLE.SLOT_WIDTH
      });
      currentTime = addHours(currentTime, 1);
      position += OVERVIEW_TABLE.SLOT_WIDTH;
    }

    while (timeSlots.length < OVERVIEW_TABLE.MIN_TIME_COLUMNS) {
      timeSlots.push({
        time: format(currentTime, 'h:mm a'),
        position,
        width: OVERVIEW_TABLE.SLOT_WIDTH
      });
      currentTime = addHours(currentTime, 1);
      position += OVERVIEW_TABLE.SLOT_WIDTH;
    }

    return { timeSlots, minTime, shouldSkipFirstColumn };
  }, [appointmentsByDentist]);

  const getAppointmentStyle = (appointment: Request) => {
    const startTime = parseTime(appointment.start_time);
    const endTime = parseTime(appointment.end_time);
    const minTimeMoment = parseTime(minTime);

    let startPosition;
    if (shouldSkipFirstColumn) {
      startPosition =
        differenceInMinutes(startTime, minTimeMoment) *
        OVERVIEW_TABLE.PIXELS_PER_MINUTE;
    } else {
      startPosition =
        (differenceInMinutes(startTime, startOfHour(minTimeMoment)) + 30) *
        OVERVIEW_TABLE.PIXELS_PER_MINUTE;
    }

    const duration = differenceInMinutes(endTime, startTime);
    const width = duration * OVERVIEW_TABLE.PIXELS_PER_MINUTE;

    return {
      left: `${startPosition}px`,
      width: `${Math.max(width, OVERVIEW_TABLE.MIN_APPOINTMENT_WIDTH)}px`
    };
  };

  return (
    <div className="overview-table">
      <div className="overview-table__dentist-list">
        <div className="overview-table__dentist-item overview-table__dentist-item-header">
          Dentists
        </div>
        {dentists.map((dentist) => (
          <div key={dentist.id} className="overview-table__dentist-item">
            <div className="overview-table__dentist-first-name">
              {formatDentistName({
                dentist,
                returnDentistFirstName: true
              })}
            </div>
            <div className="overview-table__dentist-last-name">
              {dentist.clinic?.name || dentist.last_name}
            </div>
          </div>
        ))}
      </div>

      <div className="overview-table__timeline-container">
        <div className="overview-table__timeline-header">
          {timeSlots.map((slot) => (
            <div
              key={slot.time}
              className="overview-table__time-column"
              style={{ width: `${slot.width}px` }}
            >
              {slot.time}
            </div>
          ))}
        </div>

        <div className="overview-table__timeline-body">
          {dentists.map((dentist) => {
            const dentistAppointments = dentist.id
              ? appointmentsByDentist[dentist.id] || []
              : [];

            return (
              <div key={dentist.id} className="overview-table__timeline-row">
                <div className="overview-table__timeline-grid">
                  {timeSlots.map((slot, index) => (
                    <div key={slot.time} className="overview-table__time-slot">
                      <div className="overview-table__time-half-slot">
                        {index > 0 && (
                          <div className="overview-table__grid-line-vertical" />
                        )}
                      </div>
                      <div className="overview-table__time-half-slot">
                        <div className="overview-table__grid-line-vertical" />
                      </div>
                    </div>
                  ))}
                </div>

                {dentistAppointments.map(
                  (appointment: Request, index: number) => {
                    const displayPurpose =
                      formatAppointmentPurpose(appointment);

                    return (
                      <div
                        key={appointment.id || index}
                        className="overview-table__appointment-block"
                        style={getAppointmentStyle(appointment)}
                        title={displayPurpose}
                        onClick={() => onAppointmentClick(appointment)}
                      >
                        <div className="overview-table__appointment-procedure">
                          {displayPurpose}
                        </div>
                      </div>
                    );
                  }
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default OverviewTable;
